import { createSlice } from "@reduxjs/toolkit";
import {
  fetchStaffMembers,
  addStaffMember,
  updateStaffMember,
  deleteStaffMember,
  fetchStaffMemberById,
} from "../thunks/teamMemberThunks";

const teamMemberSlice = createSlice({
  name: "staffMember",
  initialState: {
    staffMembers: {
      data: [],
      pagination: {},
      loading: false,
      error: null,
    },
    currentStaffMember: {
      data: {},
      loading: false,
      error: null,
    },
    addStaffMember: {
      loading: false,
      error: null,
      success: false,
    },
    updateStaffMember: {
      loading: false,
      error: null,
      success: false,
    },
    deleteStaffMember: {
      loading: false,
      error: null,
      success: false,
    },
  },
  reducers: {
    clearStaffMemberError: (state) => {
      state.staffMembers.error = null;
      state.currentStaffMember.error = null;
      state.addStaffMember.error = null;
      state.updateStaffMember.error = null;
      state.deleteStaffMember.error = null;
    },
    clearStaffMemberSuccess: (state) => {
      state.addStaffMember.success = false;
      state.updateStaffMember.success = false;
      state.deleteStaffMember.success = false;
    },
    resetCurrentStaffMember: (state) => {
      state.currentStaffMember.data = {};
      state.currentStaffMember.loading = false;
      state.currentStaffMember.error = null;
    },
  },
  extraReducers: (builder) => {
    // FETCH STAFF MEMBERS
    builder
      .addCase(fetchStaffMembers.pending, (state) => {
        state.staffMembers.loading = true;
        state.staffMembers.error = null;
      })
      .addCase(fetchStaffMembers.fulfilled, (state, action) => {
        state.staffMembers.loading = false;
        console.log('Fetched staff members:', action.payload.data.teamMembers);
        state.staffMembers.data = action.payload.data.teamMembers || [];
        state.staffMembers.pagination = action.payload.data.pagination || {};
      })
      .addCase(fetchStaffMembers.rejected, (state, action) => {
        state.staffMembers.loading = false;
        state.staffMembers.error =
          action.payload || "Failed to fetch staff members";
      });

    // FETCH STAFF MEMBER BY ID
    builder
      .addCase(fetchStaffMemberById.pending, (state) => {
        state.currentStaffMember.loading = true;
        state.currentStaffMember.error = null;
      })
      .addCase(fetchStaffMemberById.fulfilled, (state, action) => {
        state.currentStaffMember.loading = false;
        state.currentStaffMember.data = action.payload.data || {};
      })
      .addCase(fetchStaffMemberById.rejected, (state, action) => {
        state.currentStaffMember.loading = false;
        state.currentStaffMember.error =
          action.payload || "Failed to fetch staff member";
      });

    // ADD STAFF MEMBER
    builder
      .addCase(addStaffMember.pending, (state) => {
        state.addStaffMember.loading = true;
        state.addStaffMember.error = null;
        state.addStaffMember.success = false;
      })
      .addCase(addStaffMember.fulfilled, (state, action) => {
        state.addStaffMember.loading = false;
        state.addStaffMember.success = true;
        // Add the new staff member to the list
        if (action.payload.data) {
          state.staffMembers.data.unshift(action.payload.data);
        }
      })
      .addCase(addStaffMember.rejected, (state, action) => {
        state.addStaffMember.loading = false;
        state.addStaffMember.error =
          action.payload || "Failed to add staff member";
        state.addStaffMember.success = false;
      });

    // UPDATE STAFF MEMBER
    builder
      .addCase(updateStaffMember.pending, (state) => {
        state.updateStaffMember.loading = true;
        state.updateStaffMember.error = null;
        state.updateStaffMember.success = false;
      })
      .addCase(updateStaffMember.fulfilled, (state, action) => {
        state.updateStaffMember.loading = false;
        state.updateStaffMember.success = true;
        // Update the staff member in the list
        const index = state.staffMembers.data.findIndex(
          (member) => member._id === action.payload.data._id
        );
        if (index !== -1) {
          state.staffMembers.data[index] = action.payload.data;
        }
        // Update current staff member if it's the same one
        if (state.currentStaffMember.data._id === action.payload.data._id) {
          state.currentStaffMember.data = action.payload.data;
        }
      })
      .addCase(updateStaffMember.rejected, (state, action) => {
        state.updateStaffMember.loading = false;
        state.updateStaffMember.error =
          action.payload || "Failed to update staff member";
        state.updateStaffMember.success = false;
      });

    // DELETE STAFF MEMBER
    builder
      .addCase(deleteStaffMember.pending, (state) => {
        state.deleteStaffMember.loading = true;
        state.deleteStaffMember.error = null;
        state.deleteStaffMember.success = false;
      })
      .addCase(deleteStaffMember.fulfilled, (state, action) => {
        state.deleteStaffMember.loading = false;
        state.deleteStaffMember.success = true;
        // Remove the staff member from the list
        state.staffMembers.data = state.staffMembers.data.filter(
          (member) => member._id !== action.payload
        );
      })
      .addCase(deleteStaffMember.rejected, (state, action) => {
        state.deleteStaffMember.loading = false;
        state.deleteStaffMember.error =
          action.payload || "Failed to delete staff member";
        state.deleteStaffMember.success = false;
      });
  },
});

export const {
  clearStaffMemberError,
  clearStaffMemberSuccess,
  resetCurrentStaffMember,
} = teamMemberSlice.actions;

export default teamMemberSlice.reducer;
